import { ArrayUtils, MGPOptional, Set, Utils } from '@everyboard/lib';
import { MoveGenerator } from '../../jscaip/AI/AI';
import { CoordSet } from '../../jscaip/CoordSet';
import { Ordinal } from '../../jscaip/Ordinal';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { DiaballikMove, DiaballikBallPass, DiaballikTranslation, isTranslation } from './DiaballikMove';
import { DiaballikRules } from './DiaballikRules';
export class DiaballikMoveInConstruction {
    subMoves;
    stateBefore;
    stateAfterSubMoves;
    hasPass;
    translations;
    static finalize(m) {
        return m.finalize();
    }
    constructor(subMoves, stateBefore, stateAfterSubMoves) {
        this.subMoves = subMoves;
        this.stateBefore = stateBefore;
        this.stateAfterSubMoves = stateAfterSubMoves;
        Utils.assert(this.subMoves.length <= 3, 'DiaballikMoveInConstruction can have at most 3 submoves');
        let hasPass = false;
        let translations = 0;
        for (const subMove of subMoves) {
            if (subMove instanceof DiaballikBallPass) {
                Utils.assert(hasPass === false, 'DiaballikMoveInConstruction can have at most one pass');
                hasPass = true;
            }
            else {
                Utils.assert(translations < 2, 'DiaballikMoveInConstruction can have at most two translations');
                translations++;
            }
        }
        this.hasPass = hasPass;
        this.translations = translations;
    }
    equals(other) {
        return ArrayUtils.equals(this.subMoves, other.subMoves);
    }
    addIfLegal(subMove, listToAddTo) {
        const legality = DiaballikRules.get().isLegalSubMove(this.stateAfterSubMoves, subMove);
        if (legality.isSuccess()) {
            const newSubMoves = this.subMoves.concat([subMove]);
            this.sortIfNeeded(newSubMoves); // Sort to avoid duplicates, when possible
            const newMoveInConstruction = new DiaballikMoveInConstruction(newSubMoves, this.stateBefore, legality.get());
            listToAddTo.push(newMoveInConstruction);
        }
    }
    sortIfNeeded(subMoves) {
        // If we have to subsequent translations that do not have any coordinate in common,
        // then their order does not matter. To avoid duplicates, we sort them then.
        const firstTranslationIndex = this.getFirstTranslationIndex(subMoves);
        if (firstTranslationIndex.isAbsent()) {
            // No translation, no sort to do
            return;
        }
        const i = firstTranslationIndex.get();
        if (i + 1 >= subMoves.length || isTranslation(subMoves[i + 1]) === false) {
            // We don't have two consecutive translations, no sort to do
            return;
        }
        const firstTranslation = subMoves[i];
        const firstTranslationCoords = new CoordSet(firstTranslation.getCoords());
        const secondTranslation = subMoves[i + 1];
        const secondTranslationCoords = new CoordSet(secondTranslation.getCoords());
        const translationIntersect = firstTranslationCoords.intersection(secondTranslationCoords).size() > 0;
        if (translationIntersect) {
            // If they intersect, their order are important so we preserve it
            return;
        }
        // The "smallest" translation goes first, according to coordinate order
        // Note: we only need to compare start. Either first is bigger (and we
        // have to swap them), or it is smaller (and we keep them as is). It
        // cannot be equal, as otherwise the translations would have
        // intersected.
        const compareStart = firstTranslation.getStart().compareTo(secondTranslation.getStart());
        const firstIsBigger = compareStart > 0;
        if (firstIsBigger) {
            subMoves[i] = secondTranslation;
            subMoves[i + 1] = firstTranslation;
        }
    }
    getFirstTranslationIndex(subMoves) {
        for (let i = 0; i < subMoves.length; i++) {
            if (isTranslation(subMoves[i])) {
                return MGPOptional.of(i);
            }
        }
        return MGPOptional.empty();
    }
    getPassEnd() {
        for (const subMove of this.subMoves) {
            if (subMove instanceof DiaballikBallPass) {
                return MGPOptional.of(subMove.getEnd());
            }
        }
        return MGPOptional.empty();
    }
    passPathContains(coord) {
        for (const subMove of this.subMoves) {
            if (subMove instanceof DiaballikBallPass) {
                const passPath = subMove.getJumpedOverCoords();
                return passPath.some((c) => c.equals(coord));
            }
        }
        return false;
    }
    getPreviousTranslation() {
        for (const subMove of this.subMoves) {
            if (subMove instanceof DiaballikTranslation) {
                return MGPOptional.of(subMove);
            }
        }
        return MGPOptional.empty();
    }
    /**
     * Checks if this move has a previous translation that is the opposite of (start, end)
     */
    hasOppositeTranslation(start, end) {
        if (this.translations > 0) {
            const previousTranslation = this.getPreviousTranslation().get();
            return previousTranslation.getStart().equals(end) && previousTranslation.getEnd().equals(start);
        }
        else {
            return false;
        }
    }
    finalize() {
        Utils.assert(this.subMoves.length > 0, 'DiaballikMoveInConstruction can only be finalized if it contains something');
        const first = this.subMoves[0];
        let second = MGPOptional.empty();
        if (this.subMoves.length > 1) {
            second = MGPOptional.of(this.subMoves[1]);
        }
        let third = MGPOptional.empty();
        if (this.subMoves.length > 2) {
            third = MGPOptional.of(this.subMoves[2]);
        }
        const move = new DiaballikMove(first, second, third);
        Utils.assert(DiaballikRules.get().isLegal(move, this.stateBefore).isSuccess(), 'DiaballikMoveGenerator should only generate legal moves');
        return move;
    }
}
export class DiaballikMoveGenerator extends MoveGenerator {
    avoidDuplicates;
    constructor(avoidDuplicates = true) {
        super();
        this.avoidDuplicates = avoidDuplicates;
    }
    getListMoves(node, config) {
        const emptyMove = new DiaballikMoveInConstruction([], node.gameState, node.gameState);
        let movesInConstruction = [emptyMove];
        let moves = new Set();
        for (let i = 0; i < 3; i++) {
            let nextMovesInConstruction = [];
            for (const move of movesInConstruction) {
                const newMovesInConstruction = this.addAllPossibleSubMoves(move);
                moves = moves.unionList(newMovesInConstruction.map(DiaballikMoveInConstruction.finalize));
                nextMovesInConstruction = nextMovesInConstruction.concat(newMovesInConstruction);
            }
            movesInConstruction = nextMovesInConstruction;
        }
        return this.removeDuplicates(node.gameState, moves, config);
    }
    removeDuplicates(state, moves, config) {
        if (this.avoidDuplicates === false) {
            return moves.toList();
        }
        let seenStates = new Set();
        const movesToKeep = [];
        const rules = DiaballikRules.get();
        for (const move of moves) {
            const legalityInfo = DiaballikRules.get().isLegal(move, state);
            const stateAfterMove = rules.applyLegalMove(move, state, config, legalityInfo.get());
            if (seenStates.contains(stateAfterMove) === false) {
                movesToKeep.push(move);
                seenStates = seenStates.addElement(stateAfterMove);
            }
        }
        return movesToKeep;
    }
    addAllPossibleSubMoves(moveInConstruction) {
        const state = moveInConstruction.stateAfterSubMoves;
        const player = state.getCurrentPlayer();
        const nextMovesInConstruction = [];
        for (const coordAndContent of state.getCoordsAndContents()) {
            const coord = coordAndContent.coord;
            const piece = coordAndContent.content;
            if (piece.owner === player) {
                if (piece.holdsBall && moveInConstruction.hasPass === false) {
                    // Note: we can only do one pass per turn, hence the check on hasPass
                    for (const end of this.getPassEnds(state, coordAndContent.coord)) {
                        // The pass here is valid by construction
                        const pass = DiaballikBallPass.from(coord, end).get();
                        moveInConstruction.addIfLegal(pass, nextMovesInConstruction);
                    }
                }
                else {
                    // Can only do two translations per move
                    const hasLessThanTwoTranslations = moveInConstruction.translations < 2;
                    if (hasLessThanTwoTranslations) {
                        for (const end of this.getTranslationEnds(state, coordAndContent.coord)) {
                            // We ignore this one if we already did the opposite translation
                            const doesNotHaveOppositeTranslation = moveInConstruction.hasOppositeTranslation(coordAndContent.coord, end) === false;
                            if (doesNotHaveOppositeTranslation || this.avoidDuplicates === false) {
                                let keep = true;
                                if (moveInConstruction.hasPass) {
                                    // There was a pass before, so we only keep this if
                                    //   - it is the piece that passed, or
                                    const isPieceThatPassed = moveInConstruction.getPassEnd().equalsValue(coord);
                                    //   - it goes through the path of the pass,
                                    const goesThroughPassPath = moveInConstruction.passPathContains(end);
                                    keep = isPieceThatPassed || goesThroughPassPath;
                                }
                                if (keep || this.avoidDuplicates === false) {
                                    // The translation here is valid and legal by construction
                                    const translation = DiaballikTranslation.from(coord, end).get();
                                    moveInConstruction.addIfLegal(translation, nextMovesInConstruction);
                                }
                            }
                        }
                    }
                }
            }
        }
        return nextMovesInConstruction;
    }
    /**
     * Returns all legal pass ends for a pass starting at start
     */
    getPassEnds(state, start) {
        const player = state.getCurrentPlayer();
        const opponent = state.getCurrentOpponent();
        const ends = [];
        // A pass is in any direction, as long as it reaches a player piece and is not obstructed
        for (const direction of Ordinal.factory.all) {
            let coord = start.getNext(direction);
            while (state.coordIsNotOwnedBy(coord, opponent)) {
                const piece = state.getPieceAt(coord);
                if (piece.owner === player) {
                    ends.push(coord);
                    break;
                }
                coord = coord.getNext(direction);
            }
        }
        return ends;
    }
    /**
     * Returns all legal translation ends for a translation starting at start
     */
    getTranslationEnds(state, start) {
        const ends = [];
        // A legal translation is an orthogonal translation that ends on an empty space
        for (const direction of Orthogonal.factory.all) {
            const end = start.getNext(direction);
            if (state.isEmptyAt(end)) {
                ends.push(end);
            }
        }
        return ends;
    }
}
